import React, { useMemo, useState } from 'react';
import styles from './ChatHeader.module.scss';

import SearchInput from 'component/atom/forms/input/searchInput/SearchInput';
import Button from 'component/atom/forms/button/Button';
import BotSelector from './bot-selector/BotSelector';
import Modal from 'component/hoc/modal/Modal';
import SvgWithBranding from 'component/hoc/branding/withBrandingColor.hoc';
import { ChatItem } from '../chat-body/Chats';

const isSameDay = (a, b) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const getChatId = (c) =>
  c?.chatID ?? c?.chatId ?? c?.id ?? c?.conversationId ?? '';

const getChatTitle = (c) =>
  c?.chatTitle ?? c?.title ?? c?.name ?? c?.subject ?? 'Untitled chat';

const getChatDate = (c) => {
  const raw = c?.createdOn;

  const d = raw ? new Date(raw) : null;
  return d instanceof Date && !Number.isNaN(d?.getTime()) ? d : null;
};

const ChatHeader = ({
  isExpanded,
  onNewChatClick,
  isUserChat,
  chatsData = [],
  onChatSelect,
  onSearch,
  refetchChatsList,
  isOpen,
  botsLoading,
  botsFetching,
  bots = [],
}) => {
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [query, setQuery] = useState('');

  const expanded =
    typeof isExpanded === 'boolean'
      ? isExpanded
      : typeof isOpen === 'boolean'
        ? isOpen
        : true;

  const closeModal = () => setIsSearchOpen(false);
  const openModal = () => setIsSearchOpen(true);

  const filtered = useMemo(() => {
    const q = (query || '').trim().toLowerCase();
    if (!q) return chatsData || [];
    return (chatsData || []).filter((c) =>
      getChatTitle(c).toLowerCase().includes(q),
    );
  }, [chatsData, query]);

  const grouped = useMemo(() => {
    const today = new Date();
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

    const todayArr = [];
    const prev7Arr = [];

    filtered.forEach((c) => {
      const d = getChatDate(c);
      if (!d) return prev7Arr.push(c);

      if (isSameDay(d, today)) return todayArr.push(c);
      if (d >= sevenDaysAgo) return prev7Arr.push(c);

      prev7Arr.push(c);
    });

    return { todayArr, prev7Arr };
  }, [filtered]);

  const handleSearchChange = (_name, value) => {
    setQuery(value);
    onSearch?.(value);
  };

  const handlePickChat = (chat) => {
    onChatSelect?.(chat);
    closeModal();
  };

  const handleNewChat = () => {
    onNewChatClick?.();
    closeModal();
  };

  return (
    <div
      className={`${styles['chat-header']} ${
        expanded ? styles['expand'] : styles['collapse']
      }`}
    >
      {isUserChat ? (
        <div className={styles.botSelectorRow}>
          <BotSelector
            sidebarOpen={isOpen}
            bots={bots}
            isLoading={botsLoading}
            isFetching={botsFetching}
          />
        </div>
      ) : null}

      <div className={styles['navigation-container']}>
        <div
          className={`${styles['navigation']} ${
            expanded ? styles['expand'] : styles['collapse']
          }`}
        >
          {isOpen && !isUserChat ? (
            <div className={styles['left-container']}>
              <h2>Chat History</h2>
            </div>
          ) : null}

          <div
            className={`${styles['right-container']} ${
              !isOpen ? styles['closed-container'] : ''
            }`}
          >
            <Button
              label={isOpen ? 'New chat' : ''}
              className={styles.button}
              type='plus'
              onClick={onNewChatClick}
            />
            <button
              type='button'
              className={styles.searchTrigger}
              onClick={openModal}
            >
              <span className={styles.searchTriggerIcon}>
                <SvgWithBranding
                  type='search_input'
                  className={styles['svg']}
                />
              </span>
            </button>
          </div>
        </div>
      </div>

      <Modal
        isOpen={isSearchOpen}
        onClose={closeModal}
        title='Search chat'
        classname={styles.searchModal}
        showCancel={false}
        showSubmit={false}
        isWithHeader={false}
      >
        <div className={styles.searchModalBody}>
          <div className={styles.searchModalTop}>
            <SearchInput
              containerClassName={styles.searchModalInput}
              onChange={handleSearchChange}
              placeholder='Search chat'
              value={query}
            />
            <button
              type='button'
              className={styles.searchModalClear}
              onClick={closeModal}
              aria-label='Clear'
            >
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='24'
                height='24'
                viewBox='0 0 24 24'
                fill='none'
              >
                <path
                  d='M12 2C6.4775 2 2 6.47688 2 12C2 17.5231 6.47688 22 12 22C17.5231 22 22 17.5231 22 12C22 6.47688 17.5231 2 12 2ZM12 20.77C7.175 20.77 3.25 16.825 3.25 12C3.25 7.175 7.175 3.25 12 3.25C16.825 3.25 20.75 7.175 20.75 12C20.75 16.825 16.825 20.77 12 20.77ZM15.5356 8.465C15.2919 8.22125 14.8962 8.22125 14.6519 8.465L12.0006 11.1163L9.34937 8.465C9.10562 8.22125 8.70937 8.22125 8.465 8.465C8.22062 8.70875 8.22125 9.105 8.465 9.34875L11.1163 12L8.465 14.6513C8.22125 14.895 8.22125 15.2913 8.465 15.535C8.70875 15.7788 9.105 15.7788 9.34937 15.535L12.0006 12.8837L14.6519 15.535C14.8956 15.7788 15.2912 15.7788 15.5356 15.535C15.78 15.2913 15.7794 14.895 15.5356 14.6513L12.8844 12L15.5356 9.34875C15.78 9.10438 15.78 8.70875 15.5356 8.465Z'
                  fill='#011211'
                />
              </svg>
            </button>
          </div>

          <div className={styles.searchModalDivider} />

          <button
            type='button'
            className={styles.searchModalNewChat}
            onClick={handleNewChat}
          >
            <span className={styles.searchModalPlus}>
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='18'
                height='18'
                viewBox='0 0 18 18'
                fill='none'
              >
                <path
                  d='M1.75 9.33301H16.75M9.25 1.83301V16.833'
                  stroke='#0957D0'
                  strokeWidth='1.5'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
            </span>
            <span>New Chat</span>
          </button>

          <div className={styles.searchModalList}>
            {grouped.todayArr.map((c) => {
              const id = getChatId(c);
              const title = getChatTitle(c);

              return (
                <ChatItem
                  key={id || title}
                  id={id}
                  text={title}
                  onClick={() => handlePickChat(c)}
                  refetchChatsList={refetchChatsList}
                />
              );
            })}

            {grouped.prev7Arr?.length > 0 && (
              <div className={styles.searchModalSection}>
                <p className={styles.searchModalSectionTitle}>
                  Previous 7 Days
                </p>
                {grouped.prev7Arr.map((c) => {
                  const id = getChatId(c);
                  const title = getChatTitle(c);

                  return (
                    <ChatItem
                      key={id || title}
                      id={id}
                      text={title}
                      onClick={() => handlePickChat(c)}
                    />
                  );
                })}
              </div>
            )}

            {!grouped.todayArr?.length && !grouped.prev7Arr?.length ? (
              <p className={styles.searchModalEmpty}>No chats found.</p>
            ) : null}
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ChatHeader;
